import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import './Auth.css';

function SignIn() {
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for success message passed from registration
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the location state after showing the message
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  const handleSignInChange = (e) => {
    const { name, value } = e.target;
    setSignInData({
      ...signInData,
      [name]: value
    });
    // Clear error when user starts typing again
    if (error) setError('');
  };
  
  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Make API request to login endpoint
      const response = await fetch(buildApiUrl('api/users/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: signInData.email,
          password: signInData.password
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to sign in');
      }
      
      // Store user data in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Show success feedback
      console.log('Login successful:', data);
      
      // Redirect to dashboard
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An error occurred during sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container signin-container">
        <div className="nebula-bg auth-bg"></div>
        
        <div className="auth-card">
          <Link to="/" className="auth-logo">
            <span className="logo-text">COSMIC<span>BUILDER</span></span>
          </Link>
          
          <h1>Welcome Back</h1>
          <p className="auth-subtitle">Sign in to continue your cosmic journey</p>
          
          {success && (
            <div className="auth-success">
              <i className="fas fa-check-circle"></i> {success}
            </div>
          )}
          
          {error && (
            <div className="auth-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
          
          <form onSubmit={handleSignInSubmit} className="auth-form">
            <div className="form-group-auth">
              <label htmlFor="signin-email">Email</label>
              <input
                type="email"
                id="signin-email"
                name="email"
                value={signInData.email}
                onChange={handleSignInChange}
                required
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>
            
            <div className="form-group-auth">
              <label htmlFor="signin-password">Password</label>
              <input
                type="password"
                id="signin-password"
                name="password"
                value={signInData.password}
                onChange={handleSignInChange}
                required
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>
            
            <div className="form-options">
              <div className="remember-me">
                <input type="checkbox" id="remember" />
                <label htmlFor="remember">Remember me</label>
              </div>
              <Link to="/forgot-password" className="forgot-password">Forgot password?</Link>
            </div>
            
            <button 
              type="submit" 
              className={`auth-submit-btn ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              <span className="btn-glow"></span>
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          
          <div className="auth-separator">
            <span>OR</span>
          </div>
          
          <div className="social-auth">
            <button className="social-btn google" disabled={loading}>
              <i className="fab fa-google"></i> Sign in with Google
            </button>
            <button className="social-btn github" disabled={loading}>
              <i className="fab fa-github"></i> Sign in with GitHub
            </button>
          </div>
          
          <div className="auth-redirect">
            Don't have an account? 
            <Link to="/signup">Sign Up</Link>
          </div>
        </div>
        
        {/* Add animated background elements */}
        <div className="floating-elements auth-floating">
          <div className="floating-element planet-small"></div>
          <div className="floating-element star-cluster-small"></div>
        </div>
        
        <div className="auth-particles">
          {[...Array(20)].map((_, index) => (
            <div key={index} className="particle"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SignIn; 